package io.horion.cv.viewModel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.cv.api.datasource.common.PersonalInformation
import io.horion.cv.api.datasource.common.Signature
import io.horion.cv.api.datasource.common.Type
import io.horion.cv.api.datasource.cv.*
import io.horion.cv.api.datasource.response.Response
import io.horion.cv.api.usecase.CvUseCase
import io.horion.cv.uiState.CvUiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.files.File
import org.w3c.files.FileReader

class UpdateCvViewModel(
    private val cvUseCase: CvUseCase,
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main)
) {

    var cvUiState by mutableStateOf(CvUiState())
        private set

    var cvTitle by mutableStateOf("Cv sans titre")
        private set

    var showValidationWarning by mutableStateOf(false)
        private set

    var missingFieldIds by mutableStateOf<List<String>>(emptyList())
        private set

    var editingFormationId by mutableStateOf<String?>(null)
    var editingExperienceId by mutableStateOf<String?>(null)
    var editingCompetenceId by mutableStateOf<String?>(null)
    var editingCenterInterestId by mutableStateOf<String?>(null)
    var editingActivityId by mutableStateOf<String?>(null)
    var editingQualityId by mutableStateOf<String?>(null)
    var editingCourseId by mutableStateOf<String?>(null)
    var editingCertificatId by mutableStateOf<String?>(null)
    var editingReferenceId by mutableStateOf<String?>(null)
    var editingStageId by mutableStateOf<String?>(null)
    var editingLanguageId by mutableStateOf<String?>(null)

    fun loadCv(cvData: OrionCV, model: Model = Model.SIMPLE, title: String = "Cv sans titre") {
        cvUiState = CvUiState(data = cvData, model = model)
        cvTitle = title
    }

    fun updateCvTitle(title: String) {
        cvTitle = title
    }

    fun validateRequiredFields(): Boolean {
        val pi = cvUiState.data.personalInformation
        val missing = mutableListOf<String>()

        if (pi.lastName.isBlank()) missing.add("lastName")
        if (pi.firstName.isBlank()) missing.add("firstName")
        if (pi.bp.isBlank()) missing.add("bp")
        if (pi.email.isBlank()) missing.add("email")
        if (pi.phone.isBlank()) missing.add("phone")
        if (pi.city.isBlank()) missing.add("city")

        missingFieldIds = missing
        showValidationWarning = missing.isNotEmpty()
        return missing.isEmpty()
    }

    fun dismissValidationWarning() {
        showValidationWarning = false
    }

    fun updatePersonalInfo(update: (PersonalInformation) -> PersonalInformation) {
        val currentData = cvUiState.data
        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalInformation = update(currentData.personalInformation)
            )
        )
        if (showValidationWarning) {
            validateRequiredFields()
            if (missingFieldIds.isEmpty()) showValidationWarning = false
        }
    }

    fun updateModel(model: Model) {
        cvUiState = cvUiState.copy(model = model)
    }

    fun updateType(type: Type) {
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(type = type))
    }

    fun updateProfil(profilHtml: String) {
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(profil = profilHtml))
    }

    fun updatePhoto(image: File) {
        val reader = FileReader()
        reader.onload = { event ->
            val arrayBuffer = event.target.asDynamic().result as ArrayBuffer
            val bytes = Int8Array(arrayBuffer).unsafeCast<ByteArray>()
            cvUiState = cvUiState.copy(images = bytes)
        }
        reader.readAsArrayBuffer(image)
    }

    fun updateFormation(index: Int, formation: Formation) {
        val list = cvUiState.data.formations.toMutableList()
        if (index in list.indices) list[index] = formation else list.add(formation)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(formations = list))
    }

    fun deleteFormation(index: Int) {
        val list = cvUiState.data.formations.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(formations = list)) }
    }

    fun updateExperience(index: Int, experience: Experience) {
        val list = cvUiState.data.experiences.toMutableList()
        if (index in list.indices) list[index] = experience else list.add(experience)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(experiences = list))
    }

    fun deleteExperience(index: Int) {
        val list = cvUiState.data.experiences.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(experiences = list)) }
    }

    fun updateCompetence(index: Int, competence: Competence) {
        val list = cvUiState.data.competences.toMutableList()
        if (index in list.indices) list[index] = competence else list.add(competence)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(competences = list))
    }

    fun deleteCompetence(index: Int) {
        val list = cvUiState.data.competences.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(competences = list)) }
    }

    fun updateHobby(index: Int, hobby: String) {
        val list = cvUiState.data.hobbies.toMutableList()
        if (index in list.indices) list[index] = hobby else list.add(hobby)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(hobbies = list))
    }

    fun deleteHobby(index: Int) {
        val list = cvUiState.data.hobbies.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(hobbies = list)) }
    }

    fun updateActivity(index: Int, activity: Activity) {
        val list = cvUiState.data.extracurricularActivities.toMutableList()
        if (index in list.indices) list[index] = activity else list.add(activity)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(extracurricularActivities = list))
    }

    fun deleteActivity(index: Int) {
        val list = cvUiState.data.extracurricularActivities.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(extracurricularActivities = list)) }
    }

    fun updateQuality(index: Int, quality: String) {
        val list = cvUiState.data.qualities.toMutableList()
        if (index in list.indices) list[index] = quality else list.add(quality)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(qualities = list))
    }

    fun deleteQuality(index: Int) {
        val list = cvUiState.data.qualities.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(qualities = list)) }
    }

    fun updateCourse(index: Int, learn: Learn) {
        val list = cvUiState.data.learns.toMutableList()
        if (index in list.indices) list[index] = learn else list.add(learn)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(learns = list))
    }

    fun deleteCourse(index: Int) {
        val list = cvUiState.data.learns.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(learns = list)) }
    }

    fun updateCertificate(index: Int, cert: Certificate) {
        val list = cvUiState.data.certificates.toMutableList()
        if (index in list.indices) list[index] = cert else list.add(cert)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(certificates = list))
    }

    fun deleteCertificate(index: Int) {
        val list = cvUiState.data.certificates.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(certificates = list)) }
    }

    fun updateReference(index: Int, ref: Reference) {
        val list = cvUiState.data.references.toMutableList()
        if (index in list.indices) list[index] = ref else list.add(ref)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(references = list))
    }

    fun deleteReference(index: Int) {
        val list = cvUiState.data.references.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(references = list)) }
    }

    fun updateStage(index: Int, stage: Stage) {
        val list = cvUiState.data.stages.toMutableList()
        if (index in list.indices) list[index] = stage else list.add(stage)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(stages = list))
    }

    fun deleteStage(index: Int) {
        val list = cvUiState.data.stages.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(stages = list)) }
    }

    fun updateAchievements(text: String) {
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(achievements = listOf(text)))
    }

    fun updateSignature(city: String?, date: String?, image: String?) {
        val current = cvUiState.data.signature ?: Signature("", "", "")
        val updated = current.copy(
            city = city ?: current.city,
            date = date ?: current.date,
            image = image ?: current.image
        )
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(signature = updated))
    }

    fun updateLanguage(index: Int, name: String?, level: String?) {
        val list = cvUiState.data.languages.toMutableList()
        val existing = list.getOrNull(index) ?: LanguageItem(Language.FRENCH, Level.BEGINNER)
        val newLang = if (name != null) try { Language.valueOf(name.uppercase()) } catch (_: Exception) { existing.language } else existing.language
        val newLevel = if (level != null) try { Level.valueOf(level.uppercase()) } catch (_: Exception) { existing.level } else existing.level
        val updated = LanguageItem(language = newLang, level = newLevel)
        if (index in list.indices) list[index] = updated else list.add(updated)
        cvUiState = cvUiState.copy(data = cvUiState.data.copy(languages = list))
    }

    fun deleteLanguage(index: Int) {
        val list = cvUiState.data.languages.toMutableList()
        if (index in list.indices) { list.removeAt(index); cvUiState = cvUiState.copy(data = cvUiState.data.copy(languages = list)) }
    }

    fun updateDestination() {
        cvUiState = cvUiState.copy(
            data = cvUiState.data.copy(
                personalInformation = cvUiState.data.personalInformation.copy(state = " ", country = " ")
            )
        )
    }

    fun submitUpdate() {
        scope.launch {
            cvUiState = cvUiState.copy(loading = true, error = null, success = null)
            updateDestination()
            try {
                if (cvUiState.images != null) {
                    val result = cvUseCase.uploadImage(cvUiState.images)
                    if (result?.success == true) {
                        updatePersonalInfo { it.copy(photo = result.message) }
                    }
                }

                val response = cvUseCase.updateCv(
                    cv = cvUiState.data,
                    model = cvUiState.model.name
                )

                when {
                    response == null -> {
                        cvUiState = cvUiState.copy(isConnected = false, loading = false)
                    }
                    response.success -> {
                        cvUiState = cvUiState.copy(
                            cvCreateSuccess = true,
                            loading = false,
                            success = "CV mis à jour"
                        )
                    }
                    else -> {
                        cvUiState = cvUiState.copy(loading = false, error = response.message)
                    }
                }
            } catch (e: Exception) {
                cvUiState = cvUiState.copy(loading = false, error = e.message)
            }
        }
    }
}
